
(async function () {
  const KEY = '__notimThemeButtonIntegration';

  // Prevent duplicate initialization.
  if (window[KEY]) {
    return;
  }

  const CONFIG = {
    themeButton: '[id="main-product-handle-btn"]',
    notimContainer: '.na-back-in-stock-container',
    mobileStickyBar: '.hdpb-bar',
    mobileBreakpoint: 767
  };

  if (!location.pathname.includes('/products/')) return;

  const state = {
    variants: new Map(),
    lastVariantId: null,
    lastAvailability: null,
    placeholder: null,
    notimElement: null,
    observer: null,
    timer: null,
    updating: false,
    observing: false
  };

  window[KEY] = state;


  // -------------------------------------------
  // 0. Wait until Notim app is fully rendered
  // -------------------------------------------

  function waitForNotimApp() {
    return new Promise(resolve => {

      const isNotimReady = () => {
        const container = document.querySelector(
          CONFIG.notimContainer
        );

        const button = container?.querySelector(
          '.nm-btn-container .nm-btn'
        );

        const themeButton = document.querySelector(
          CONFIG.themeButton
        );

        return Boolean(
          container &&
          container.isConnected &&
          button &&
          button.isConnected &&
          themeButton
        );
      };

      let observer;
      let checking = false;

      const checkReady = () => {
        if (!isNotimReady() || checking) return;

        checking = true;

        // Wait for the browser to complete two render frames.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            checking = false;

            if (!isNotimReady()) return;

            observer?.disconnect();

            console.debug('[Notim] App loaded successfully');

            resolve();
          });
        });
      };

      observer = new MutationObserver(checkReady);

      observer.observe(document.documentElement, {
        childList: true,
        subtree: true
      });

      // Handle app already loaded before our script.
      checkReady();
    });
  }

  await waitForNotimApp();

  // -------------------------------------------
  // 1. Inject CSS
  // -------------------------------------------

  const style = document.createElement('style');
  style.id = 'notim-button-position-style';

  style.textContent = `
    .notim-hide-theme-button,
    .notim-hide-container {
      display: none !important;
    }

    /* Relocated Notim container */
    .na-back-in-stock-container.notim-mounted {
      display: flex !important;
      align-items: center;
      justify-content: center;
      width: 100% !important;
      min-width: 0;
      max-width: 100%;
      margin: 0 !important;
      padding: 0 !important;
      box-sizing: border-box;
    }

    .na-back-in-stock-container.notim-mounted
    .nm-btn-container {
      width: 100% !important;
      max-width: 100%;
      margin: 0 !important;
      padding: 0 !important;
    }

    .na-back-in-stock-container.notim-mounted
    .nm-btn {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      min-width: 0 !important;
      min-height: 35px !important;
      padding: 12px 16px !important;
      margin: 0 !important;
      border-radius: 30px !important;
      font-size: 15px !important;
      font-weight: 600 !important;
      line-height: 22px !important;
      text-align: center !important;
      box-sizing: border-box !important;
      font-size: calc(20.6 * var(--u)) !important;
    }

    /* Mobile sticky bar */
    @media (max-width: 767px) {
      .hdpb-bar .na-back-in-stock-container.notim-mounted {
        width: 100% !important;
        min-width: 0 !important;
        flex: 1 1 auto;
      }

      .hdpb-bar .na-back-in-stock-container.notim-mounted
      .nm-btn {
        width: 100% !important;
        min-height: 44px !important;
        padding: 10px 12px !important;
        font-size: 13px !important;
        line-height: 20px !important;
        border-radius: 25px !important;
      }
    }
  `;

  document.getElementById(style.id)?.remove();
  document.head.appendChild(style);

  // -------------------------------------------
  // 2. Load Shopify variants
  // -------------------------------------------

  try {
    const response = await fetch(
      window.location.pathname + '.js'
    );

    if (!response.ok) {
      throw new Error('Unable to load Shopify product');
    }

    const product = await response.json();

    state.variants = new Map(
      product.variants.map(variant => [
        String(variant.id),
        Boolean(variant.available)
      ])
    );

  } catch (error) {
    console.error('[Notim]', error);
    delete window[KEY];
    return;
  }

  // -------------------------------------------
  // 3. Current variant
  // -------------------------------------------

  function getVariantId() {
    const urlVariant = new URLSearchParams(
      location.search
    ).get('variant');

    const formVariant = document.querySelector(
      'form[action*="/cart/add"] input[name="id"]'
    )?.value;

    return urlVariant || formVariant || null;
  }

  // -------------------------------------------
  // 4. Observer management
  // -------------------------------------------

  function stopObserving() {
    if (!state.observer) return;

    state.observer.disconnect();
    state.observing = false;
  }

  function startObserving() {
    if (!state.observer || !document.body) return;

    state.observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    state.observing = true;
  }

  // -------------------------------------------
  // 5. Move the existing Notim element
  // -------------------------------------------

  function moveNotim(targetButton, notimElement) {
    if (!targetButton?.parentNode) return;

    // Remember original location once.
    if (!state.placeholder) {
      const placeholder = document.createComment(
        'notim-original-position'
      );

      notimElement.parentNode.insertBefore(
        placeholder,
        notimElement
      );

      state.placeholder = placeholder;
      state.notimElement = notimElement;
    }

    // Move only if the position is actually different.
    if (
      notimElement.parentNode !== targetButton.parentNode ||
      notimElement.nextElementSibling !== targetButton
    ) {
      targetButton.parentNode.insertBefore(
        notimElement,
        targetButton
      );
    }

    if (!notimElement.classList.contains('notim-mounted')) {
      notimElement.classList.add('notim-mounted');
    }

    if (notimElement.classList.contains('notim-hide-container')) {
      notimElement.classList.remove('notim-hide-container');
    }
  }

  // -------------------------------------------
  // 6. Restore Notim to original position
  // -------------------------------------------

  function restoreNotim(notimElement) {
    const placeholder = state.placeholder;

    if (placeholder?.parentNode) {
      const inOriginalPosition =
        notimElement.parentNode === placeholder.parentNode &&
        placeholder.nextSibling === notimElement;

      if (!inOriginalPosition) {
        placeholder.parentNode.insertBefore(
          notimElement,
          placeholder.nextSibling
        );
      }
    }

    notimElement.classList.remove('notim-mounted');
    notimElement.classList.add('notim-hide-container');
  }

  // -------------------------------------------
  // 7. Update theme / Notim visibility
  // -------------------------------------------

  function updateButtons() {
    if (state.updating) return;

    const variantId = getVariantId();

    if (
      !variantId ||
      !state.variants.has(String(variantId))
    ) {
      return;
    }

    const available = state.variants.get(
      String(variantId)
    );

    const themeButtons = Array.from(
      document.querySelectorAll(CONFIG.themeButton)
    );

    const notimElement =
      state.notimElement?.isConnected
        ? state.notimElement
        : document.querySelector(CONFIG.notimContainer);

    if (!themeButtons.length || !notimElement) return;

    const isMobile = window.matchMedia(
      `(max-width: ${CONFIG.mobileBreakpoint}px)`
    ).matches;

    const targetButton = isMobile
      ? themeButtons.find(button =>
          button.closest(CONFIG.mobileStickyBar)
        ) || themeButtons[0]
      : themeButtons.find(button =>
          !button.closest(CONFIG.mobileStickyBar)
        ) || themeButtons[0];

    state.updating = true;

    // Prevent our own DOM changes from triggering the observer.
    stopObserving();

    try {
      if (available) {
        // Restore native Add to Bag buttons.
        themeButtons.forEach(button => {
          if (button.classList.contains(
            'notim-hide-theme-button'
          )) {
            button.classList.remove(
              'notim-hide-theme-button'
            );
          }
        });

        restoreNotim(notimElement);

      } else {
        // Hide native Out of Stock buttons.
        themeButtons.forEach(button => {
          if (!button.classList.contains(
            'notim-hide-theme-button'
          )) {
            button.classList.add(
              'notim-hide-theme-button'
            );
          }
        });

        // Move existing Notim into the theme button position.
        moveNotim(targetButton, notimElement);
      }

      if (
        state.lastVariantId !== String(variantId) ||
        state.lastAvailability !== available
      ) {
        console.debug('[Notim] Variant updated:', {
          variantId,
          available,
          device: isMobile ? 'mobile' : 'desktop'
        });
      }

      state.lastVariantId = String(variantId);
      state.lastAvailability = available;

    } finally {
      state.updating = false;
      startObserving();
    }
  }

  // -------------------------------------------
  // 8. Debounced updates
  // -------------------------------------------

  function scheduleUpdate() {
    clearTimeout(state.timer);

    state.timer = setTimeout(() => {
      updateButtons();
    }, 80);
  }

  // -------------------------------------------
  // 9. Shopify variant change events
  // -------------------------------------------

  document.addEventListener('change', scheduleUpdate);
  document.addEventListener('variant:change', scheduleUpdate);
  document.addEventListener('variant:changed', scheduleUpdate);

  document.addEventListener(
    'product:variant-change',
    scheduleUpdate
  );

  // -------------------------------------------
  // 10. Track history changes
  // -------------------------------------------

  ['pushState', 'replaceState'].forEach(method => {
    const original = history[method];

    history[method] = function (...args) {
      const result = original.apply(this, args);

      scheduleUpdate();

      return result;
    };
  });

  window.addEventListener('popstate', scheduleUpdate);

  // Only update on breakpoint changes, not every resize pixel.
  const mobileMedia = window.matchMedia(
    `(max-width: ${CONFIG.mobileBreakpoint}px)`
  );

  mobileMedia.addEventListener('change', scheduleUpdate);

  // -------------------------------------------
  // 11. Observe external DOM changes
  // -------------------------------------------

  state.observer = new MutationObserver(mutations => {
    if (state.updating) return;

    const relevant = mutations.some(mutation => {
      const nodes = [
        ...mutation.addedNodes,
        ...mutation.removedNodes
      ];

      return nodes.some(node => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
          return false;
        }

        const selectors = [
          CONFIG.themeButton,
          CONFIG.notimContainer,
          CONFIG.mobileStickyBar
        ].join(',');

        return (
          node.matches?.(selectors) ||
          node.querySelector?.(selectors)
        );
      });
    });

    if (relevant) {
      scheduleUpdate();
    }
  });

  startObserving();

  // -------------------------------------------
  // 12. Initial update
  // -------------------------------------------

  updateButtons();

})();
